/**
 * FileGateway 的组装与能力探测（M1-16 / DESIGN.md §2.6、§4.7）。
 */
import { DownloadGateway, domDownloadDriver, type DownloadDriver } from './download';
import { FsaGateway, type FsaPickers } from './fsa';
import type { FileGateway } from './types';

export {
  isAbortError,
  saveActionLabel,
  throwIfAborted,
  type FileGateway,
  type FileHandleRef,
  type GatewayCallOptions,
  type GatewayKind,
  type OpenedFile,
  type SavePayload,
} from './types';
export { FsaGateway, type FsaFileHandleLike, type FsaPickers } from './fsa';
export { DownloadGateway, domDownloadDriver, type DownloadDriver } from './download';
export {
  OpfsWorkspace,
  type RecoverableEntry,
  type WorkspaceDirLike,
  type WorkspaceFileHandleLike,
} from './workspace';

export interface SelectGatewayResult {
  readonly gateway: FileGateway;
  /** 为何选中它。降级时 UI 据此提示（DESIGN.md §2.6：显示「保存」或「下载副本…」，ADR-0016）。 */
  readonly reason: string;
}

/**
 * 按能力选择面向用户的 FileGateway。
 *
 * 探测的是**两个** picker 都存在且是函数：Safari 的部分版本只有 OPFS 没有
 * picker，Firefox 两个都没有；只能打开不能保存等于不能用 FSA 的核心价值
 * （原地覆盖），所以缺任何一个都整体降级到下载模式。
 *
 * @param globals 探测对象，默认全局。测试用它注入 Firefox/Safari 形状的环境。
 * @param driver  降级时使用的下载驱动，默认真实 DOM 驱动。
 */
export function selectGateway(
  globals: Record<string, unknown> = globalThis as unknown as Record<string, unknown>,
  driver?: DownloadDriver,
): SelectGatewayResult {
  const openPicker = globals['showOpenFilePicker'];
  const savePicker = globals['showSaveFilePicker'];

  if (typeof openPicker === 'function' && typeof savePicker === 'function') {
    const pickers: FsaPickers = {
      // bind 到探测对象上：真实环境里这俩是 window 方法，裸调会 Illegal invocation
      showOpenFilePicker: openPicker.bind(globals) as FsaPickers['showOpenFilePicker'],
      showSaveFilePicker: savePicker.bind(globals) as FsaPickers['showSaveFilePicker'],
    };
    return {
      gateway: new FsaGateway(pickers),
      reason: 'File System Access 可用，支持原地保存（Ctrl+S 覆盖原文件）',
    };
  }

  return {
    gateway: new DownloadGateway(driver ?? domDownloadDriver()),
    reason: 'File System Access 不可用（Firefox/Safari），降级为下载，保存动作显示「下载副本…」',
  };
}
